import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Menu,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core';
import { IconFilter2 } from '@tabler/icons-react';

import { useGetPatientRecallsQuery } from '@/store/slices/api/patient/recallsApiSlice';
import formatDate from '@/utils/formatDate';
import InfoMessage from '@/components/InfoMessage';

import { ActiveDot } from '@/pages/common/ActiveDot';

import styles from './Recalls.module.scss';

import type { PatientRecall } from '@/store/slices/api/patient/recallsApiSlice';

function RecallReason({ text }: { text?: string | null }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [clamped, setClamped] = useState(false);
  const value = text?.trim() || '—';

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [value]);

  const body = (
    <Text ref={ref} size="sm" lineClamp={2} className={clamped ? styles.reasonClamped : undefined}>
      {value}
    </Text>
  );

  if (!clamped) return body;

  return (
    <Tooltip label={value} multiline maw={360} withArrow position="bottom-start" openDelay={200}>
      {body}
    </Tooltip>
  );
}

interface FilterMenuProps {
  selectedFilters: string[];
  onApplyFilters: (filters: string[]) => void;
  availableOptions: string[];
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  selectedFilters,
  onApplyFilters,
  availableOptions
}) => {
  const [tempSelectedFilters, setTempSelectedFilters] = useState<string[]>(selectedFilters);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const hasActiveFilters = selectedFilters.length > 0;

  const handleFilterToggle = (filterOption: string) => {
    setTempSelectedFilters((prev) =>
      prev.includes(filterOption) ? prev.filter((f) => f !== filterOption) : [...prev, filterOption]
    );
  };

  const handleApply = useCallback(() => {
    if (isApplying) return;

    setIsApplying(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onApplyFilters(tempSelectedFilters);

      setTimeout(() => {
        setIsApplying(false);
        setIsMenuOpen(false);
      }, 200);
    }, 300);
  }, [tempSelectedFilters, onApplyFilters, isApplying]);

  const handleCancel = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setTempSelectedFilters(selectedFilters);
    setIsMenuOpen(false);
    setIsApplying(false);
  };

  const handleMenuOpen = () => {
    setTempSelectedFilters(selectedFilters);
    setIsMenuOpen(true);
    setIsApplying(false);
  };

  const handleMenuClose = () => {
    if (!isApplying) {
      setIsMenuOpen(false);
      setIsApplying(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Menu
      shadow="md"
      width={200}
      position="bottom-start"
      opened={isMenuOpen}
      onClose={handleMenuClose}
      closeOnItemClick={false}>
      <Menu.Target>
        <ActionIcon variant="subtle" size="sm" onClick={handleMenuOpen}>
          <Box pos="relative">
            <IconFilter2 size={14} />
            {hasActiveFilters && <ActiveDot sx={{ position: 'absolute', right: -2, top: -2 }} />}
          </Box>
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown className={styles.filtersMenu}>
        {availableOptions.map((option) => (
          <div
            key={option}
            className={`${styles.filterOption} ${tempSelectedFilters.includes(option) ? styles.selectedOption : ''}`}>
            <label className={styles.filterCheckbox}>
              <Checkbox
                checked={tempSelectedFilters.includes(option)}
                onChange={() => !isApplying && handleFilterToggle(option)}
                mr={10}
                variant="outline"
              />
              <span className={styles.filterLabel}>{option}</span>
            </label>
          </div>
        ))}

        <div className={styles.filterActions}>
          <Group gap="sm" justify="flex-end" p="6">
            <Button
              variant="subtle"
              size="xs"
              onClick={handleCancel}
              color="gray"
              disabled={isApplying}>
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={handleApply}
              loading={isApplying}
              disabled={isApplying}
              loaderProps={{ size: 12 }}>
              Apply
            </Button>
          </Group>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};

const Recalls = ({ patientId }: { patientId: string }) => {
  const { data: recallsData, isLoading, error } = useGetPatientRecallsQuery({ patientId });
  const [classFilter, setClassFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" style={{ minHeight: '200px' }}>
        <Loader size="lg" />
        <Text>Loading patient recalls...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <InfoMessage
        title={'Failed To Load Recalls'}
        message={'Please refresh the page or contact support if the issue persists.'}
      />
    );
  }

  const recalls = recallsData || [];

  // Get unique values for filter options
  const uniqueClasses = Array.from(new Set(recalls.map((recall) => recall.recallClass)));
  const uniqueStatuses = Array.from(new Set(recalls.map((recall) => recall.recallStatus)));
  const uniqueTypes = Array.from(new Set(recalls.map((recall) => recall.type.recallType)));

  // Filter recalls based on selected filters
  const filteredRecalls = recalls
    .filter((recall) => {
      const classMatch = classFilter.length === 0 || classFilter.includes(recall.recallClass);
      const statusMatch = statusFilter.length === 0 || statusFilter.includes(recall.recallStatus);
      const typeMatch = typeFilter.length === 0 || typeFilter.includes(recall.type.recallType);
      return classMatch && statusMatch && typeMatch;
    })
    .sort((a, b) => {
      // Sort by recallDate descending (date format: YYYY-MM-DD)
      if (a.recallDate < b.recallDate) return 1;
      if (a.recallDate > b.recallDate) return -1;
      return 0;
    });

  const handleClassFilterChange = (filters: string[]) => {
    setClassFilter(filters);
    setPage(1);
  };

  const handleStatusFilterChange = (filters: string[]) => {
    setStatusFilter(filters);
    setPage(1);
  };

  const handleTypeFilterChange = (filters: string[]) => {
    setTypeFilter(filters);
    setPage(1);
  };

  const recallPageSize = 10;
  const recallPages = Math.max(1, Math.ceil(filteredRecalls.length / recallPageSize));
  const pagedRecalls = filteredRecalls.slice((page - 1) * recallPageSize, page * recallPageSize);

  const tableHeader = () => (
    <Table.Thead>
      <Table.Tr>
        <Table.Th w="120px" className={styles.tableHeader}>
          DATE
        </Table.Th>
        <Table.Th w="80px" className={styles.tableHeader}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            TYPE
            {recalls?.length > 0 && (
              <FilterMenu
                selectedFilters={typeFilter}
                onApplyFilters={handleTypeFilterChange}
                availableOptions={uniqueTypes}
              />
            )}
          </Group>
        </Table.Th>
        <Table.Th w="100px" className={styles.tableHeader}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            CLASS
            {recalls?.length > 0 && (
              <FilterMenu
                selectedFilters={classFilter}
                onApplyFilters={handleClassFilterChange}
                availableOptions={uniqueClasses}
              />
            )}
          </Group>
        </Table.Th>
        <Table.Th w="100px" className={styles.tableHeader}>
          <Group gap="xs" wrap="nowrap" justify="space-between">
            STATUS
            {recalls?.length > 0 && (
              <FilterMenu
                selectedFilters={statusFilter}
                onApplyFilters={handleStatusFilterChange}
                availableOptions={uniqueStatuses}
              />
            )}
          </Group>
        </Table.Th>
        <Table.Th w="120px" className={styles.tableHeader}>
          RECALL NUMBER
        </Table.Th>
        <Table.Th className={styles.tableHeader}>REASON</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const renderRecallRows = (recallsList: PatientRecall[]) => {
    return recallsList.map((recall, index) => (
      <Table.Tr key={`${recall.deviceId}-${recall.recallDate}-${index}`}>
        <Table.Td w="120px">
          <Text size="sm">{formatDate(recall.recallDate, 'MM/DD/YY')}</Text>
        </Table.Td>
        <Table.Td w="140px">
          <Text size="sm">{`${recall.type.deviceType} (${recall.type.recallType})`}</Text>
          <Text size="xs" c="dimmed">
            {recall.type.deviceName}
          </Text>
        </Table.Td>
        <Table.Td w="100px">{recall.recallClass}</Table.Td>
        <Table.Td w="100px">{recall.recallStatus}</Table.Td>
        <Table.Td w="120px">{recall.recallNumber}</Table.Td>
        <Table.Td>
          <RecallReason text={recall.reasonForRecall} />
        </Table.Td>
      </Table.Tr>
    ));
  };

  const tableBody = () => {
    if (!filteredRecalls || filteredRecalls.length === 0) {
      const hasActiveFilters =
        classFilter.length > 0 || statusFilter.length > 0 || typeFilter.length > 0;

      return (
        <Table.Tr>
          <Table.Td colSpan={5} align="center" py="xl">
            <Text c="dimmed">
              {hasActiveFilters
                ? `No recalls found for selected filters`
                : 'No recalls found for this patient'}
            </Text>
          </Table.Td>
        </Table.Tr>
      );
    }

    return renderRecallRows(pagedRecalls);
  };

  return (
    <ScrollArea scrollbarSize={5}>
      <Table withColumnBorders={false} stickyHeader>
        {tableHeader()}
        <Table.Tbody>{tableBody()}</Table.Tbody>
      </Table>
      {recallPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={recallPages} value={page} onChange={setPage} size="sm" color="plum" />
        </Group>
      )}
    </ScrollArea>
  );
};

export default Recalls;
